import NextLink from 'next/link';
import PropTypes from 'prop-types';
import { Fragment, forwardRef, memo } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';

import Box from 'shopper/components/Box';
import Heading from 'shopper/components/Heading';
import Icon from 'shopper/components/Icon';
import Link from 'shopper/components/Link';
import Text from 'shopper/components/Text';

import { isLastItem } from 'lib/array';
import { getDeepSubcategoryParent } from 'lib/category';
import { toCategoryLink, toSubcategoryLink } from 'lib/links';
import { noop } from 'lib/utils';

const CategoryNav = forwardRef(
  (
    {
      activeSubcategory = null,
      categoryName,
      categorySlug = null,
      className,
      isSpaced = false,
      subcategories,
      onClose = noop,
      onNavItemClick,
    },
    ref
  ) => {
    const onNavItemClickHandler = (name) => {
      onNavItemClick(name);
      onClose();
    };

    return (
      <Box
        ref={ref}
        as="nav"
        className={twMerge(!isSpaced && 'p-4', className)}
        data-test-selector="categories-nav"
      >
        <div className={twJoin('flex items-start', isSpaced ? 'mb-4' : 'mb-2')}>
          {categorySlug ? (
            <>
              <NextLink
                href={toCategoryLink(categorySlug)}
                prefetch={false}
                passHref
              >
                <Icon
                  as="a"
                  className="mr-2"
                  name="arrow-backwards"
                  size="size1"
                  onClick={() => onNavItemClickHandler(categoryName)}
                />
              </NextLink>
              <NextLink
                href={toCategoryLink(categorySlug)}
                prefetch={false}
                passHref
              >
                <Heading
                  as="a"
                  size="size5"
                  onClick={() => onNavItemClickHandler(categoryName)}
                >
                  {categoryName}
                </Heading>
              </NextLink>
            </>
          ) : (
            <Heading as="span" size="size5">
              {categoryName}
            </Heading>
          )}
        </div>
        {subcategories?.map(
          ({
            deepSubcategories,
            subcategoryId,
            subcategoryName,
            subcategorySlug,
          }) => (
            <Fragment key={subcategoryId}>
              {/* eslint-disable-next-line no-nested-ternary */}
              {activeSubcategory?.subcategoryId === subcategoryId ? (
                <Text
                  className={twJoin(
                    isSpaced ? 'mt-6' : 'mt-2',
                    activeSubcategory?.subcategoryId === subcategoryId &&
                      'text-primary-400 dark:text-primary-200'
                  )}
                  data-test-selector="active-subcategory-page"
                  size="size1"
                  bold
                >
                  {subcategoryName}
                </Text>
              ) : getDeepSubcategoryParent(activeSubcategory)?.subcategoryId ===
                subcategoryId ? (
                <NextLink
                  href={toSubcategoryLink(subcategorySlug)}
                  prefetch={false}
                  passHref
                >
                  <Text
                    as="a"
                    className={isSpaced ? 'mt-6' : 'mt-2'}
                    data-test-selector="goto-subcategory-page"
                    size="size1"
                    bold
                    onClick={() => onNavItemClickHandler(subcategoryName)}
                  >
                    {subcategoryName}
                  </Text>
                </NextLink>
              ) : (
                <NextLink
                  href={toSubcategoryLink(subcategorySlug)}
                  prefetch={false}
                  passHref
                >
                  <Link
                    className={isSpaced ? 'mt-6' : 'mt-2'}
                    data-test-selector="goto-subcategory-page"
                    size="size1"
                    type="secondary"
                    onClick={() => onNavItemClickHandler(subcategoryName)}
                  >
                    {subcategoryName}
                  </Link>
                </NextLink>
              )}
              {deepSubcategories?.length > 0 && (
                <div
                  className={twJoin(
                    'flex flex-col items-start gap-y-2 border-0 border-l border-solid border-neutral-low-100 dark:border-neutral-high-400',
                    isSpaced ? 'mt-4' : 'mt-2'
                  )}
                >
                  {deepSubcategories.map(
                    (
                      {
                        deepSubcategoryId,
                        deepSubcategoryName,
                        deepSubcategorySlug,
                        isSelected,
                      },
                      index
                    ) => (
                      <Fragment key={deepSubcategoryId}>
                        {isSelected ? (
                          <Text
                            className={twJoin(
                              'text-bold pl-4 text-primary-400 dark:text-primary-200',
                              isLastItem(deepSubcategories, index) && 'mb-2'
                            )}
                            data-test-selector="active-deepsubcategory-page"
                          >
                            {deepSubcategoryName}
                          </Text>
                        ) : (
                          <NextLink
                            key={deepSubcategoryId}
                            href={toSubcategoryLink(deepSubcategorySlug)}
                            prefetch={false}
                            passHref
                          >
                            <Link
                              className={twJoin(
                                'pl-4',
                                isLastItem(deepSubcategories, index) && 'mb-2'
                              )}
                              data-test-selector="goto-deepsubcategory-page"
                              type="secondary"
                              onClick={() =>
                                onNavItemClickHandler(deepSubcategoryName)
                              }
                            >
                              {deepSubcategoryName}
                            </Link>
                          </NextLink>
                        )}
                      </Fragment>
                    )
                  )}
                </div>
              )}
            </Fragment>
          )
        )}
      </Box>
    );
  }
);

CategoryNav.displayName = 'CategoryNav';

CategoryNav.propTypes = {
  activeSubcategory: PropTypes.shape({
    articles: PropTypes.arrayOf(
      PropTypes.shape({
        articleId: PropTypes.number.isRequired,
        articleDescription: PropTypes.string.isRequired,
        articleSlug: PropTypes.string.isRequired,
        articleTitle: PropTypes.string.isRequired,
      })
    ),
    category: PropTypes.shape({
      categoryId: PropTypes.number.isRequired,
      categoryName: PropTypes.string.isRequired,
      categorySlug: PropTypes.string.isRequired,
      subcategories: PropTypes.arrayOf(
        PropTypes.shape({
          isSelected: PropTypes.bool.isRequired,
          subcategoryId: PropTypes.number.isRequired,
          subcategoryName: PropTypes.string.isRequired,
          subcategorySlug: PropTypes.string.isRequired,
          deepSubcategories: PropTypes.arrayOf(
            PropTypes.shape({
              deepSubcategoryId: PropTypes.number.isRequired,
              deepSubcategoryName: PropTypes.string.isRequired,
              deepSubcategorySlug: PropTypes.string.isRequired,
              isSelected: PropTypes.bool.isRequired,
            })
          ),
        })
      ),
    }).isRequired,
    metaDescription: PropTypes.string,
    metaTitle: PropTypes.string,
    pageDescription: PropTypes.string,
    pageTitle: PropTypes.string,
    stores: PropTypes.arrayOf(
      PropTypes.shape({
        storeId: PropTypes.number.isRequired,
        storeName: PropTypes.string.isRequired,
      })
    ).isRequired,
    subcategoryId: PropTypes.number.isRequired,
    subcategoryName: PropTypes.string.isRequired,
    subcategorySlug: PropTypes.string.isRequired,
  }),
  categoryName: PropTypes.string.isRequired,
  categorySlug: PropTypes.string,
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  isSpaced: PropTypes.bool,
  subcategories: PropTypes.arrayOf(
    PropTypes.shape({
      isSelected: PropTypes.bool,
      subcategoryId: PropTypes.number.isRequired,
      subcategoryName: PropTypes.string.isRequired,
      subcategorySlug: PropTypes.string.isRequired,
      deepSubcategories: PropTypes.arrayOf(
        PropTypes.shape({
          deepSubcategoryId: PropTypes.number.isRequired,
          deepSubcategoryName: PropTypes.string.isRequired,
          deepSubcategorySlug: PropTypes.string.isRequired,
          isSelected: PropTypes.bool,
        })
      ),
    })
  ).isRequired,
  onClose: PropTypes.func,
  onNavItemClick: PropTypes.func.isRequired,
};

export default memo(CategoryNav);
